/**
 * Pure utility/helper functions for VisionAI
 */

/**
 * Format a number as a compact string (1000 → 1K, 1000000 → 1M)
 * @param {number} n
 * @returns {string}
 */
export function formatCompact(n) {
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(n);
}

/**
 * Format a date as a human-readable string
 * @param {string|Date} date
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatDate(date, options = { year: 'numeric', month: 'long', day: 'numeric' }) {
    return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
}

/**
 * Clamp a number between min and max
 */
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Truncate a string to a given length, appending "…" if trimmed
 */
export function truncate(str, maxLength = 150) {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength).trimEnd() + '…';
}

/**
 * Convert a string to a URL-friendly slug
 */
export function slugify(str) {
    return str
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/**
 * Capitalise the first letter of a string
 */
export function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Debounce a function call
 * @param {Function} fn
 * @param {number} delay ms
 * @returns {Function}
 */
export function debounce(fn, delay) {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
    };
}

/**
 * Build a class string from multiple conditional values (lightweight clsx alternative)
 * @param {...(string|undefined|null|false)} classes
 * @returns {string}
 */
export function cn(...classes) {
    return classes.filter(Boolean).join(' ');
}

/**
 * Validate an email address
 */
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Calculate estimated reading time for blog content
 * @param {string} content
 * @param {number} [wpm=200]
 * @returns {string}
 */
export function readingTime(content, wpm = 200) {
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / wpm);
    return `${minutes} min read`;
}
